import { startOfDay, isValid } from 'date-fns';

/**
 * Gera uma agenda de visitas respeitando datas e limites.
 *
 * @param {string} startDateStr Data de início do período.
 * @param {string} endDateStr Data de fim do período.
 * @param {number} frequencyAmount Número de visitas por dia (Frequência).
 * @param {number} totalAmount Número máximo de visitas no período (Limite Total).
 * @param {string} hospitalReleasePrevision Data de referência/lançamento.
 * @returns {Array<{date: string, total: number}>} Array de objetos de agendamento.
 */
function _findVisitScheduleReference(params) {
  let { startDate, endDate, frequencyAmount, totalAmount, hospitalReleasePrevision } = params;

  // ============================================
  // --- 1. Validação e Conversão de Entradas ---
  // ============================================

  startDate = new Date(startDate);
  endDate = new Date(endDate);
  hospitalReleasePrevision = new Date(hospitalReleasePrevision);

  if (!startDate || !endDate || !hospitalReleasePrevision) {
    return [];
  }

  if (!isValid(startDate) || !isValid(endDate) || !isValid(hospitalReleasePrevision)) {
    return [];
  }

  if (frequencyAmount <= 0 || totalAmount <= 0) {
    throw new Error('Erro: Frequência e Total devem ser maiores que zero.');
  }

  if (startDate.getTime() > endDate.getTime()) {
    throw new Error('A data de início é posterior à data de fim. Retornando array vazio.');
  }

  // =================================================
  // --- 2. Determinação da Data de Início Efetiva ---
  // =================================================

  // A data atual (hoje) é definida como meia-noite UTC para comparação
  const now = new Date();
  const todayUtc = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));

  // A data de início efetiva é a mais recente entre startDate, hospitalReleasePrevision e todayUtc
  let effectiveStartDate = startDate;

  if (hospitalReleasePrevision.getTime() > effectiveStartDate.getTime()) {
    effectiveStartDate = hospitalReleasePrevision;
  }

  if (todayUtc.getTime() > effectiveStartDate.getTime()) {
    effectiveStartDate = todayUtc;
  }

  // =================================
  // --- 3. Geração do Agendamento ---
  // =================================

  // Se a data de início efetiva for posterior à data de fim, não há agendamento possível
  if (effectiveStartDate.getTime() > endDate.getTime()) {
    throw new Error(
      'A data de início efetiva é posterior à data de fim. Nenhuma visita pode ser programada.'
    );
  }

  const schedule = [];
  let currentTotalVisits = 0;
  // Clonamos a data para garantir que a iteração não altere a data inicial
  let currentDate = new Date(effectiveStartDate.getTime());

  while (currentDate.getTime() <= endDate.getTime() && currentTotalVisits < totalAmount) {
    let visitsForThisDay = frequencyAmount;
    const remainingVisits = totalAmount - currentTotalVisits;

    // 4. Ajustar se a frequência for maior que o restante (atingiu o limite total)
    if (visitsForThisDay > remainingVisits) {
      visitsForThisDay = remainingVisits;
    }

    // Adicionar ao agendamento se houver visitas a agendar
    if (visitsForThisDay > 0) {
      schedule.push({
        date: startOfDay(currentDate).toISOString(),
        total: visitsForThisDay
      });
      currentTotalVisits += visitsForThisDay;
    }

    // 5. Avançar para o próximo dia (Adiciona 1 dia UTC)
    currentDate.setUTCDate(currentDate.getUTCDate() + 1);
  }

  return schedule;
}

export { _findVisitScheduleReference };
